// PKCE deep-link auth callback handling for the native wrappers.
//
// On iOS/Android the OAuth/magic-link redirect returns to the app via the custom
// scheme `com.zeichenapp.app://auth-callback?code=<pkce_code>` (see
// capacitor.config.ts). The Capacitor App `appUrlOpen` listener hands us that
// URL; we extract the PKCE `code` and exchange it for a Supabase session.
//
// The parse + exchange logic is pure and dependency-injected (PkceExchanger), so
// it is unit-tested without Capacitor or the supabase SDK. The listener wiring
// lives in index.ts (the untested composition edge).

export const AUTH_CALLBACK_SCHEME = 'com.zeichenapp.app';
export const AUTH_CALLBACK_HOST = 'auth-callback';

export interface AuthCallback {
  code: string;
}

/** Minimal slice of supabase-js auth needed to complete a PKCE exchange. */
export interface PkceExchanger {
  exchangeCodeForSession(authCode: string): Promise<{ error: { message: string } | null }>;
}

export type CallbackResult = 'exchanged' | 'ignored' | 'error' | 'auth_error';

export interface AuthError {
  error: string;
  description?: string;
}

/** True if the URL is our auth-callback deep link (regardless of payload). */
export function isAuthCallbackUrl(rawUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }
  if (url.protocol.replace(/:$/, '') !== AUTH_CALLBACK_SCHEME) return false;
  const firstSegment = url.pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  return url.hostname === AUTH_CALLBACK_HOST || firstSegment === AUTH_CALLBACK_HOST;
}

/** Extract an OAuth error (`?error=...&error_description=...`) from the callback. */
export function parseAuthError(rawUrl: string): AuthError | null {
  if (!isAuthCallbackUrl(rawUrl)) return null;
  const params = new URL(rawUrl).searchParams;
  const error = params.get('error');
  if (!error) return null;
  const description = params.get('error_description');
  return description ? { error, description } : { error };
}

/**
 * Parse a deep-link URL into the PKCE code, or null if it is not our auth
 * callback (or carries no usable code). Tolerant of `://host/path` and
 * `://host?query` shapes that custom schemes produce on iOS vs Android.
 */
export function parseAuthCallback(rawUrl: string): AuthCallback | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  // Custom-scheme URLs parse with protocol "com.zeichenapp.app:" — strip the
  // trailing colon for comparison.
  const proto = url.protocol.replace(/:$/, '');
  if (proto !== AUTH_CALLBACK_SCHEME) return null;
  // The host slot of a custom-scheme URL is unreliable across platforms; accept
  // the callback when EITHER host or the first path segment is auth-callback.
  const firstSegment = url.pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  if (url.hostname !== AUTH_CALLBACK_HOST && firstSegment !== AUTH_CALLBACK_HOST) return null;

  const code = url.searchParams.get('code');
  if (!code) return null;
  return { code };
}

/**
 * Handle an incoming deep-link URL: ignore non-callback URLs, exchange the PKCE
 * code otherwise. Never throws — returns a result the caller can log.
 */
export async function handleAuthCallback(
  rawUrl: string,
  exchanger: PkceExchanger,
): Promise<CallbackResult> {
  if (!isAuthCallbackUrl(rawUrl)) return 'ignored';
  // A provider-reported error (user cancelled, access_denied, …) — surface it so
  // the app can show a message instead of silently doing nothing.
  if (parseAuthError(rawUrl)) return 'auth_error';
  const parsed = parseAuthCallback(rawUrl);
  if (!parsed) return 'ignored';
  try {
    const { error } = await exchanger.exchangeCodeForSession(parsed.code);
    return error ? 'error' : 'exchanged';
  } catch {
    return 'error';
  }
}
